/**
 * usePublicArtifactSelection
 *
 * Derives the selected public artifact descriptor from the URL
 * (`/share/:token/:singular?/:itemId?` and the embed equivalent) — selection is
 * URL-driven so the detail panel is shareable and the browser back button
 * closes it. The slim LIST payload resolves the descriptor (kind + ids +
 * title); the full detail is then fetched lazily by `usePublicArtifactDetail`.
 * Items are matched by `shortId` (what map clicks navigate with) falling back
 * to `id` (list rows or hand-crafted URLs).
 *
 * @module components/share/public/usePublicArtifactSelection
 */
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { findArtifactIdBySingular } from '@/artifacts/navigation-map';
import type { ArtifactId, ArtifactSingular } from '@/artifacts/navigation-map';
import type { PublicShareListData } from '@/types/share';

/**
 * Kind of a selectable public artifact. Derived from the artifact registry's
 * singular set so a new artifact type can't silently go unhandled here — adding
 * one widens this union and makes `kindToArtifactId`'s switch fail to compile.
 */
export type PublicArtifactKind = ArtifactSingular;

/**
 * URL-resolved reference to the open public artifact. Carries just enough to
 * open the detail panel, drive the navigation tab strip, and fetch the full
 * detail by id — the heavy DTO is loaded separately.
 */
export interface PublicArtifactDescriptor {
  kind: PublicArtifactKind;
  id: string;
  shortId: string;
  title: string;
}

/** Maps an artifact kind to its artifact registry id (for navigation). */
export function kindToArtifactId(kind: PublicArtifactKind): ArtifactId {
  switch (kind) {
    case 'task':
      return 'tasks';
    case 'note':
      return 'notes';
    case 'obstruction':
      return 'obstructions';
  }
}

/**
 * Builds a descriptor from a single list item + its kind. The one construction
 * site for the `{ kind, id, shortId, title }` shape — shared by the URL
 * resolver below and the list rows (`PublicArtifactList`), so the shape has a
 * single definition.
 */
export function itemToDescriptor(
  kind: PublicArtifactKind,
  item: { id: string; shortId: string; title: string }
): PublicArtifactDescriptor {
  return { kind, id: item.id, shortId: item.shortId, title: item.title };
}

function matchByIds<T extends { id: string; shortId: string }>(
  items: T[],
  itemId: string
): T | undefined {
  return items.find((item) => item.shortId === itemId || item.id === itemId);
}

/** Resolves the descriptor for a kind by matching `itemId` against its list rows. */
function toDescriptor<T extends { id: string; shortId: string; title: string }>(
  kind: PublicArtifactKind,
  items: T[],
  itemId: string
): PublicArtifactDescriptor | null {
  const item = matchByIds(items, itemId);
  return item ? itemToDescriptor(kind, item) : null;
}

export function usePublicArtifactSelection(
  listData: PublicShareListData | null
): PublicArtifactDescriptor | null {
  const { singular, itemId } = useParams<{
    singular?: string;
    itemId?: string;
  }>();

  return useMemo(() => {
    if (!singular || !itemId || !listData) return null;

    switch (findArtifactIdBySingular(singular)) {
      case 'tasks':
        return toDescriptor('task', listData.tasks, itemId);
      case 'notes':
        return toDescriptor('note', listData.notes, itemId);
      case 'obstructions':
        return toDescriptor('obstruction', listData.obstructions, itemId);
      default:
        return null;
    }
  }, [singular, itemId, listData]);
}
